import React from 'react';
import { connect, MapStateToProps } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { ExtendedMatchEntity } from '../types';
import { ReduxState } from 'app/redux/types';

import { Badge, BadgeColor, BadgeSize } from 'app/design-system/legacy-components/Badge';
import type { RecordOfBetEntity } from 'app/lib/betting/betting-entities';
import { SlotType } from 'app/lib/betting/betting-entities';
import { getTruncatedNumber } from 'app/lib/formatters';
import getTextWithHighlightFragment from 'app/lib/getTextWithHighlightFragment';
import { replaceDividerOnDash, replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import { composeMatchCenterPath, getPrefixBySlotType } from 'app/modules/Navigation/utils';
import {
  matches,
  selectMatchFinishing,
  selectWinningBets,
} from 'app/modules/Search/selectors';

import Bet from './Bet';
import MatchDetails from './MatchDetails';
import MatchSubheader from './MatchSubheader';

import styles from 'app/modules/Search/Match/MatchItem.module.scss';

type StoreProps = {
  id?: number;
  sportId?: number;
  tournamentUnionId?: number;
  tournamentId?: number;
  tournamentStageId?: number;
  isFirst?: boolean;
  betsCount?: number;
  name?: string;
  slotType?: number;
  winningBets?: Array<null | RecordOfBetEntity>;
  finished?: boolean;
};

type Props = {
  searchValue: string;
  matchId: number;
  onLinkClick: VoidFunction;
  onBetClick: (matchId?: number, betId?: number) => () => void;
  style?: React.CSSProperties;
};

const Item: React.FC<Props & StoreProps> = ({
  id,
  sportId,
  tournamentUnionId,
  tournamentId,
  tournamentStageId,
  isFirst,
  betsCount,
  slotType,
  name,
  searchValue,
  onBetClick,
  style,
  winningBets,
  finished,
  onLinkClick,
}) => {
  if (!id) {
    return null;
  }

  const pathname = slotType && getPrefixBySlotType(slotType);
  const replacedMatchName = replacingHyphenOnDash(name);

  const matchIntegrity = (
    sportId
    && tournamentUnionId
    && tournamentId
    && tournamentStageId
    && id
  );

  return (
    <div className={styles.bet}
         style={style}>
      {isFirst && <MatchSubheader matchId={id} />}

      <div className={classNames(styles.matchRow, { [styles.finished]: finished })}
           data-test={`match-row-${id}`}>
        <div className={styles.match}>
          <div className={styles.matchTitle}>
            {(matchIntegrity && pathname && replacedMatchName)
              ? (
                <Link
                  className={styles.matchName}
                  onClick={onLinkClick}
                  to={composeMatchCenterPath(
                    {
                      sportId,
                      tournamentUnionId,
                      tournamentId,
                      tournamentStageId,
                      matchId: id,
                    },
                    pathname,
                  )}
                  data-ga-event="search-detailView"
                  data-ga-sport-match={id}
                  dangerouslySetInnerHTML={{
                    __html: getTextWithHighlightFragment(
                      replacedMatchName,
                      replaceDividerOnDash(searchValue), {
                        className: styles.highlight,
                      }),
                  }} />
              ) : (
                <div className={styles.matchName}>{replacedMatchName}</div>
              )}

            {(slotType === SlotType.LIVE && !finished) && (
              <Badge text="LIVE"
                     size={BadgeSize.SMALL}
                     color={BadgeColor.PRIMARY}
                     className={styles.liveBadge} />
            )}
          </div>

          <MatchDetails matchId={id} />

        </div>

        <div className={styles.betPrices}>
          {(winningBets?.length && !finished)
            ? (
              <div className={styles.stakesContainer}
                   data-test="stake-container">
                {winningBets.map(
                  (bet, index): React.ReactNode =>
                    bet
                      ? (
                        <Bet key={bet.key}
                             bet={bet}
                             matchId={id}
                             onBetClick={onBetClick} />
                      )
                      : (
                        <div key={index}
                             className={styles.stakeBlank}
                             data-test="bet-empty">
                          <span>-</span>
                          <span>-</span>
                        </div>
                      ),
                )}
              </div>
            )
            : null
          }

          {(betsCount && !finished)
            ? (
              <div className={styles.betsCount}>
                {matchIntegrity && pathname
                  ? (
                    <Link to={
                      composeMatchCenterPath(
                        {
                          sportId,
                          tournamentUnionId,
                          tournamentId,
                          tournamentStageId,
                          matchId: id,
                        },
                        pathname)}
                          onClick={onLinkClick}>
                      {`+${getTruncatedNumber(betsCount, { accuracy: { thousands: 1 } })}`}
                    </Link>
                  )
                  : betsCount
                }
              </div>
            )
            : null
          }
        </div>
      </div>
    </div>
  );
};

const mapStateToProps: MapStateToProps<
  StoreProps,
  Props,
  ReduxState
> = (state: ReduxState, { matchId }): StoreProps => {
  const match: ExtendedMatchEntity | undefined = matches(state).get(matchId);

  if (!match) {
    return {};
  }

  return {
    id: match.id,
    sportId: match.sportId,
    tournamentUnionId: match.tournamentUnionId,
    tournamentId: match.tournamentId,
    tournamentStageId: match.tournamentStageId,
    isFirst: match.isFirst,
    betsCount: match.betsCount,
    slotType: match.slotType,
    name: match.name,
    winningBets: selectWinningBets(state, matchId),
    finished: selectMatchFinishing(state, matchId),
  };
};

export default connect(mapStateToProps)(Item);
